import React, {FC, useState} from 'react';
import styled from "styled-components";
import Modal from "antd/es/modal/Modal";
import {Form, Input, Row, Col} from "antd";
import {defineMessages, useIntl} from "react-intl";
import CloseIcon from "../../../../../media/icons/close.svg?react";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomInputNumber} from "../../../../../components/UI/AntCustom/CustomInputNumber";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {fetchAirports, getInitialAirportForSelect} from "../../../../../utils/helpers/selectDataFetchers/fetchAirports";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonUnitMessages} from "../../../../../intl/commonMessages/CommonUnitMessages";
import {IAirport} from "../../../../../store/reducers/fleetReducer/fleetReducerTypes";

const TechnicalStopsModalMessages = defineMessages({
  title: {
    id: "technicalStopsModal.title",
    defaultMessage: "Technical Stops",
  },
  addStop: {
    id: "technicalStopsModal.addStop",
    defaultMessage: "+ Add another stop",
  },
  blockTitle: {
    id: "technicalStopsModal.blockTitle",
    defaultMessage: "BLOCK {number}",
  },
  placeLabel: {
    id: "technicalStopsModal.placeLabel",
    defaultMessage: "Location",
  },
  placePlaceholder: {
    id: "technicalStopsModal.placePlaceholder",
    defaultMessage: "Select airport",
  },
});

interface ITechnicalStop {
  id: string;
  hours?: number;
  minutes?: number;
  airportId?: number;
}

interface ITechnicalStopsModal {
  visible: boolean;
  onClose: () => void;
  onSave: (stops: ITechnicalStop[]) => void;
  initialStops?: ITechnicalStop[];
}

const StyledModal = styled(Modal)`
  z-index: 2000 !important;
  
  &.ant-modal {
    z-index: 2000 !important;
  }
  
  .ant-modal-content {
    border-radius: 12px;
    padding: 0;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-close {
    display: none;
  }
  
  .ant-modal-mask {
    z-index: 1999 !important;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 16px 10px 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderTitle = styled.div`
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 22px;
  color: #000;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  width: 39px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  
  svg {
    width: 16px;
    height: 16px;
    
    path {
      fill: #808080;
    }
  }
  
  &:hover {
    opacity: 0.7;
  }
`;

const ModalContent = styled.div`
  padding: 10px 16px 40px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StopBlock = styled.div`
  background: #F5F5F5;
  border: 1px solid #FFFFFF;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BlockTitle = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: #000;
`;

const TimeFieldsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  position: relative;
`;

const TimeFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const TimeLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: #000;
`;

const StyledInputNumber = styled(CustomInputNumber)`
  width: 130px;
  height: 47px;
  border-radius: 5px;
  
  .ant-input-number {
    width: 100%;
    height: 47px;
  }
  
  .ant-input-number-input {
    height: 47px;
    padding-right: 20px;
  }
`;

const AirportFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const AirportLabel = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: #000;
`;

const StyledDebounceSelect = styled(DebounceSelect)`
  width: 100%;
  height: 47px;
  
  .ant-select-selector {
    height: 47px !important;
    border-radius: 5px !important;
    border: 1px solid #D9DAD9 !important;
    display: flex;
    align-items: center;
  }
  
  .ant-select-selection-item {
    line-height: 47px;
    display: flex;
    align-items: center;
  }
  
  .ant-select-selection-placeholder {
    color: #A5A5A5;
    font-size: 14px;
    line-height: 47px;
    display: flex;
    align-items: center;
  }
  
  .ant-select-selection-search {
    display: flex;
    align-items: center;
    
    .ant-select-selection-search-input {
      line-height: 47px;
    }
  }
`;

const AddStopButton = styled.button`
  border: 1px solid #D9DAD9;
  border-radius: 4px;
  background: transparent;
  width: 100%;
  height: 47px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: #000;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #1890FF;
    color: #1890FF;
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SaveButton = styled(CustomButton)`
  width: 100%;
  height: 47px;
  background: #1890FF !important;
  border-radius: 5px;
  border: none !important;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: #FFFFFF;
  
  &:hover {
    background: #40a9ff !important;
  }
`;

export const TechnicalStopsModal: FC<ITechnicalStopsModal> = ({
  visible,
  onClose,
  onSave,
  initialStops = [],
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [stops, setStops] = useState<ITechnicalStop[]>(
    initialStops.length > 0 
      ? initialStops 
      : [{ id: '1', hours: 5, minutes: 35 }]
  );

  const handleAddStop = () => {
    const newId = String(stops.length + 1);
    setStops([...stops, { id: newId }]);
  };

  const handleSave = () => {
    form.validateFields().then(() => {
      const values = form.getFieldsValue();
      const formattedStops: ITechnicalStop[] = stops.map((stop, index) => {
        const hoursField = `hours_${stop.id}`;
        const minutesField = `minutes_${stop.id}`;
        const airportField = `airport_${stop.id}`;
        
        return {
          id: stop.id,
          hours: values[hoursField],
          minutes: values[minutesField],
          airportId: values[airportField],
        };
      });
      
      onSave(formattedStops);
      onClose();
    });
  };


  return (
    <StyledModal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={501}
      centered
      maskClosable={false}
    >
      <ModalHeader>
        <HeaderTitle>{intl.formatMessage(TechnicalStopsModalMessages.title)}</HeaderTitle>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </ModalHeader>

      <ModalContent>
        <StyledForm form={form} layout="vertical">
          {stops.map((stop, index) => (
            <StopBlock key={stop.id}>
              <BlockTitle>
                {intl.formatMessage(TechnicalStopsModalMessages.blockTitle, { number: index + 1 })}
              </BlockTitle>

              <TimeFieldsRow>
                <TimeFieldWrapper>
                  <TimeLabel>{intl.formatMessage(CommonFormMessages.hoursLabel)}</TimeLabel>
                  <Form.Item
                    name={`hours_${stop.id}`}
                    initialValue={stop.hours}
                    rules={[
                      { required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired) },
                      { min: 0, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinimumZero) },
                      { max: 99, type: "number", message: intl.formatMessage(CommonFormMessages.ruleHugeNumber) },
                    ]}
                  >
                    <StyledInputNumber
                      placeholder="__"
                      addonAfter={intl.formatMessage(CommonUnitMessages.hour)}
                      maxLength={2}
                      precision={0}
                      onlyDigits
                    />
                  </Form.Item>
                </TimeFieldWrapper>

                <TimeFieldWrapper>
                  <TimeLabel>{intl.formatMessage(CommonFormMessages.minutesLabel)}</TimeLabel>
                  <Form.Item
                    name={`minutes_${stop.id}`}
                    initialValue={stop.minutes}
                    rules={[
                      { required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired) },
                      { min: 0, type: "number", message: intl.formatMessage(CommonFormMessages.ruleMinimumZero) },
                      { max: 59, type: "number", message: intl.formatMessage(CommonFormMessages.ruleHugeNumber) },
                    ]}
                  >
                    <StyledInputNumber
                      placeholder="__"
                      addonAfter={intl.formatMessage(CommonUnitMessages.minute)}
                      maxLength={2}
                      precision={0}
                      onlyDigits
                    />
                  </Form.Item>
                </TimeFieldWrapper>
              </TimeFieldsRow>

              <AirportFieldWrapper>
                <AirportLabel>{intl.formatMessage(TechnicalStopsModalMessages.placeLabel)}</AirportLabel>
                <Form.Item
                  name={`airport_${stop.id}`}
                  initialValue={stop.airportId}
                >
                  <StyledDebounceSelect
                    fetchOptions={fetchAirports}
                    placeholder={intl.formatMessage(TechnicalStopsModalMessages.placePlaceholder)}
                    showSearch
                    defaultOptionsFetchCallback={stop.airportId ? () => {
                      return fetchAirports().then(airports => 
                        airports.filter(a => a.value === stop.airportId)
                      );
                    } : undefined}
                  />
                </Form.Item>
              </AirportFieldWrapper>
            </StopBlock>
          ))}

          <ButtonsWrapper>
            <AddStopButton type="button" onClick={handleAddStop}>
              {intl.formatMessage(TechnicalStopsModalMessages.addStop)}
            </AddStopButton>

            <SaveButton type="primary" onClick={handleSave}>
              {intl.formatMessage(CommonButtonMessages.save)}
            </SaveButton>
          </ButtonsWrapper>
        </StyledForm>
      </ModalContent>
    </StyledModal>
  );
};

